import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MasterProduct } from "../../models/master_product";
import { fetchHeaderProducts, getCartItems, removeCartItem, getUserFromId } from "../../services/api_service";
import { useCart } from "../../context/cart_context";

import fullLogo from "../../assets/images/full_logo.png";
import searchIcon from "../../assets/icons/search_icon.png";
import cartIcon from "../../assets/icons/cart_icon.png";
import profileIcon from "../../assets/icons/profile_icon.png";
import closeIcon from "../../assets/images/close.png";
import MenuIcon from "../../assets/icons/menu.png";
import SearchIcon from "../../assets/svg/search.svg";

import { useMediaQuery } from "react-responsive";
import { Item } from "../../models/item_model";
import { Customer } from "../../models/customer_model";
import { toast } from "react-toastify";

const icons = [searchIcon, cartIcon, profileIcon];

const footerLinks = [
  { label: "Terms & Conditions", tab: "T&C" },
  { label: "Shipping Policy", tab: "Shipping Policy" },
  { label: "Return Policy", tab: "Return Policy" },
  { label: "Refund Policy", tab: "Refund Policy" },
  { label: "Privacy Policy", tab: "Privacy Policy" },
];

export const Header: React.FC = () => {
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const { cartItems, setCartItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem("userId");

  const [cartOpen, setCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [subtotal, setSubtotal] = useState(0);

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  // Fetch header products and cache
  useEffect(() => {
    const cached = localStorage.getItem("headerProducts");
    if (cached) {
      try {
        const data = JSON.parse(cached);
        if (Array.isArray(data) && data.length) {
          setMasterProducts(data);
          setLoading(false);
          return;
        }
      } catch {
        console.warn("Invalid header cache, fetching fresh data");
      }
    }

    const fetchAndCache = async () => {
      try {
        const products = await fetchHeaderProducts();
        setMasterProducts(products);
        localStorage.setItem("headerProducts", JSON.stringify(products));
      } catch (err) {
        console.error("Failed to fetch header products", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAndCache();

    const savedSearches = localStorage.getItem("recentSearches");
    if (savedSearches) setRecentSearches(JSON.parse(savedSearches));
  }, []);

  // Fetch cart items
  const fetchCart = async () => {
    if (!userId) return;
    setCartLoading(true);
    try {
      const res = await getCartItems();
      if (res) {
        setCartItems(res.items);
        const total = res.items.reduce(
          (sum: number, item: Item) => sum + (item.final_price * (item.added_quantity || 1)),
          0
        );
        setSubtotal(total);
      }
    } catch (e) {
      console.error("Failed to fetch cart items", e);
    } finally {
      setCartLoading(false);
    }
  };

  // Handle cart quantity change
  const handleQtyChange = (item: Item, change: number) => {
    const updated = cartItems.map((i: Item) =>
      i.item_id === item.item_id
        ? { ...i, added_quantity: Math.max(1, (i.added_quantity || 1) + change) }
        : i
    );
    setCartItems(updated);
    const total = updated.reduce(
      (sum: number, it: Item) => sum + (it.final_price * (it.added_quantity || 1)),
      0
    );
    setSubtotal(total);
  };

  // Remove item from cart
  const handleRemoveItem = async (item: Item) => {
    if (userId) {
      setCartLoading(true);
      await removeCartItem(userId, item.cart_id);
      fetchCart();
    }
  };

  // Search handlers
  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      toast("Please enter a search term");
      return;
    }
    const updated = [trimmed, ...recentSearches.filter((q) => q !== trimmed)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
    navigate("/result", { state: { search: encodeURIComponent(trimmed) } });
    setShowSearch(false);
    setSearchQuery("");
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch(searchQuery);
  };

  // Header category click
  const handleCategoryClick = (product: MasterProduct) => {
    navigate("/result", {
      state: {
        masterProductId: product.master_product_id,
        headerTitle: product.product_name,
      },
    });
  };

  // Drawer links click
  const handleLinkClick = (tab: string) => {
    if (tab) {
      setDrawerOpen(false);
      navigate(`/documents?tab=${encodeURIComponent(tab)}`);
    }
  };

  // Icon clicks
  const handleIconClick = async (idx: number) => {
    const icon = icons[idx];
    if (icon === searchIcon) {
      setShowSearch(true);
    } else if (icon === cartIcon) {
      if (!userId) return navigate("/login");
      if (isMobile) {
        navigate("/cart");
      } else {
        if (location.pathname === "/cart") return;
        setCartOpen(true);
        fetchCart();
      }
    } else if (icon === profileIcon) {
      if (!userId) navigate("/login");
      else navigate("/profile");
    }
  };

  const isCheckoutDisabled = !cartItems || cartItems.length === 0;

  if (loading) return <div style={styles.header}>Loading...</div>;

  return (
    <>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isMobile && (
            <div style={styles.drawerToggle} onClick={() => setDrawerOpen(true)}>
              <img
                src={MenuIcon}
                alt="Menu"
                style={{ width: 24, height: 24, objectFit: "contain" }}
              />
            </div>
          )}
          <img src={fullLogo} alt="Logo" style={styles.logo} onClick={() => navigate("/")} />
        </div>

        {!isMobile && (
          <div style={styles.categoriesList}>
            {masterProducts.map((prod) => (
              <button
                key={prod.master_product_id}
                style={{ ...styles.categoryButton, fontWeight: 500 }}
                onClick={() => handleCategoryClick(prod)}
              >
                {prod.product_name}
              </button>
            ))}
          </div>
        )}

        <div style={{ ...styles.iconsContainer, gap: isMobile ? "2vw" : "1vw" }}>
          {icons.map((icon, idx) => (
            <div key={idx} style={styles.iconWrapper} onClick={() => handleIconClick(idx)}>
              <img src={icon} alt={`icon-${idx}`} style={styles.icon} />
              {icon === cartIcon && userId && cartItems.length > 0 && (
                <div style={styles.cartBadge}>{cartItems.length}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Cart modal for web */}
      {!isMobile && (
        <div
          style={{
            ...styles.cartOverlay,
            visibility: cartOpen ? "visible" : "hidden",
            opacity: cartOpen ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
          onClick={() => setCartOpen(false)}
        >
          <div
            style={{
              ...styles.cartDrawer,
              transform: cartOpen ? "translateX(0)" : "translateX(100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cart header */}
            <div style={styles.cartHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={cartIcon} alt="icon-cart" style={styles.icon} />
                <h3 style={{ margin: 0, fontWeight: "normal" }}>Items in your cart</h3>
              </div>
              <img src={closeIcon} alt="close" style={{ width: 25, height: 25, cursor: "pointer" }} onClick={() => setCartOpen(false)} />
            </div>

            {/* Cart body */}
            <div style={styles.cartBody}>
              {cartLoading ? (
                <div style={styles.spinnerContainer}>
                  <div style={styles.spinner}></div>
                </div>
              ) : cartItems.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40 }}>No items in cart</div>
              ) : (
                cartItems.map((item: Item, idx: number) => (
                  <React.Fragment key={idx}>
                    <div style={styles.cartItem}>
                      <img src={item.item_images[0].document_url} alt={item.title} style={styles.cartItemImage} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ fontWeight: 600 }}>{item.title}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span
                            onClick={() => handleRemoveItem(item)}
                            style={{ cursor: "pointer", color: "#e91e63", fontSize: 14, fontWeight: 500 }}
                          >
                            🗑️
                          </span>
                          <div style={{ display: "flex", alignItems: "center", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden" }}>
                            <button style={styles.qtyBtn} onClick={() => handleQtyChange(item, -1)}>-</button>
                            <div style={styles.qtyBox}>{item.added_quantity || 1}</div>
                            <button style={styles.qtyBtn} onClick={() => handleQtyChange(item, 1)}>+</button>
                          </div>
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, minWidth: 60, textAlign: "right" }}>₹{item.final_price}</div>
                    </div>
                    <div style={styles.divider}></div>
                  </React.Fragment>
                ))
              )}
            </div>

            {/* Cart footer */}
            <div style={styles.cartFooter}>
              <div style={styles.subtotalRow}>
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <span style={{ fontSize: 13, display: "flex", justifyContent: "center" }}>
                Shipping, taxes, and discount codes calculated at checkout.
              </span>
              <button
                style={{ ...styles.checkoutBtn, opacity: isCheckoutDisabled ? 0.5 : 1, cursor: isCheckoutDisabled ? "not-allowed" : "pointer" }}
                disabled={isCheckoutDisabled}
                onClick={() => {
                  navigate("/cart");
                  setCartOpen(false);
                }}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Overlay */}
      {showSearch && (
        <div style={styles.searchOverlay} onClick={() => setShowSearch(false)}>
          <div style={styles.searchWrapper} onClick={(e) => e.stopPropagation()}>
            <div style={styles.searchContainer}>
              <input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={handleKeyDown} style={styles.searchInput} />
              <button style={styles.searchBtn} onClick={() => handleSearch(searchQuery)}>
                <img src={SearchIcon} alt="search" style={{ width: 20, height: 20 }} />
              </button>
            </div>

            {recentSearches.length > 0 && (
              <div style={styles.recentContainer}>
                {recentSearches.map((q, idx) => (
                  <div key={idx} style={styles.recentItem} onClick={() => handleSearch(q)}>{q}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Drawer for mobile */}
      {drawerOpen && (
        <div style={{ ...styles.drawerOverlay, display: drawerOpen ? "flex" : "none" }} onClick={() => setDrawerOpen(false)}>
          <div style={{ ...styles.drawer, transform: drawerOpen ? "translateX(0)" : "translateX(-100%)" }} onClick={(e) => e.stopPropagation()}>
            {userId && (
              <>
                {customerLoading ? (
                  <div style={{ padding: 20 }}>Loading user...</div>
                ) : customer ? (
                  <div style={styles.profileBox}>
                    <div style={styles.avatarCircle}>{customer.user_name?.charAt(0).toUpperCase()}</div>
                    <div>
                      <div style={styles.userName}>{customer.user_name}</div>
                      <div style={styles.userMobile}>{customer.mobile_number}</div>
                    </div>
                  </div>
                ) : null}

                <div style={styles.sectionTitle}>Personal</div>
                <div onClick={() => { setDrawerOpen(false); navigate("/profile?section=Profile") }} style={styles.listItem}>👤 Profile</div>
                <div onClick={() => { setDrawerOpen(false); navigate("/profile?section=Orders") }} style={styles.listItem}>📦 Orders</div>
                <div onClick={() => { setDrawerOpen(false); navigate("/cart") }} style={styles.listItem}>🛒 My Cart</div>
                <div onClick={() => { setDrawerOpen(false); navigate("/profile?section=Address") }} style={styles.listItem}>🏠 Saved Address</div>
              </>
            )}

            <div style={styles.sectionTitle}>Policies</div>
            {footerLinks.map((link, index) => (
              <div key={index} style={styles.footerLink} onClick={() => handleLinkClick(link.tab)}>
                {link.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// Styles (merged)
const styles: { [key: string]: React.CSSProperties } = {
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1vh 4vw", backgroundColor: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", position: "sticky", top: 0, zIndex: 1000, height: "8vh", minHeight: 50 },
  logo: { height: "5vh", maxHeight: 40, width: "auto", maxWidth: "36vw", cursor: "pointer", objectFit: "contain", flexShrink: 1 },
  iconsContainer: { display: "flex", alignItems: "center" },
  iconWrapper: { backgroundColor: "#f5f5f5", borderRadius: "50%", padding: "1vh", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative" },
  icon: { height: "2.6vh", width: "2.6vh", objectFit: "contain" },
  drawerToggle: { fontSize: 24, cursor: "pointer", userSelect: "none" },
  categoriesList: { display: "flex", gap: 15, overflowX: "auto", flex: 1, marginLeft: 30 },
  categoryButton: { fontSize: 17, fontWeight: 400, color: "#333", background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap" },
  cartBadge: { position: "absolute", top: -5, right: -5, backgroundColor: "#e91e63", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },

  // Cart
  cartOverlay: { position: "fixed", top: 0, left: 0, bottom: 0, right: 0, backgroundColor: "rgba(0,0,0,0.3)", zIndex: 2000, display: "flex", justifyContent: "flex-end", fontFamily: "Arimo, sans-serif" },
  cartDrawer: { width: "40vw", maxWidth: 500, backgroundColor: "#fff", height: "100%", display: "flex", flexDirection: "column", transform: "translateX(100%)", transition: "transform 0.3s ease-in-out", boxShadow: "-2px 0 8px rgba(0,0,0,0.2)" },
  cartHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: 20, borderBottom: "1px solid #eee" },
  cartBody: { flex: 1, overflowY: "auto", padding: 20 },
  cartItem: { display: "flex", gap: 15, padding: "10px 0", alignItems: "center" },
  cartItemImage: { width: 70, height: 70, borderRadius: 8, objectFit: "cover" },
  qtyBtn: { borderColor: "#fff", padding: "4px 8px", cursor: "pointer" },
  qtyBox: { minWidth: 32, textAlign: "center", padding: "2px 6px" },
  divider: { height: 1, backgroundColor: "#eee", margin: "8px 0" },
  cartFooter: { padding: 20, borderTop: "1px solid #eee", backgroundColor: "#fff", boxShadow: "0 -2px 6px rgba(0,0,0,0.1)" },
  subtotalRow: { display: "flex", justifyContent: "space-between", marginBottom: 15, fontSize: 16 },
  checkoutBtn: { width: "100%", padding: "14px", border: "none", borderRadius: 8, backgroundColor: "#e91e63", color: "#fff", fontSize: 16, fontWeight: 600, marginTop: "10px" },
  spinnerContainer: { display: "flex", justifyContent: "center", alignItems: "center", height: 150 },
  spinner: { width: 40, height: 40, border: "4px solid #f3f3f3", borderTop: "4px solid #e91e63", borderRadius: "50%", animation: "spin 1s linear infinite" },

  // Search
  searchOverlay: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.2)", backdropFilter: "blur(2px)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "10%", zIndex: 9999 },
  searchWrapper: { width: "80%", maxWidth: 600 },
  searchContainer: { display: "flex", width: "100%", backgroundColor: "#f5f5f5", borderRadius: 15, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" },
  searchInput: { flex: 1, padding: "20px 16px", border: "none", outline: "none", backgroundColor: "#fff", fontSize: 16, fontFamily: "Arimo, sans-serif" },
  searchBtn: { backgroundColor: "#e91e63", color: "#fff", border: "none", padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 20 },
  recentContainer: { marginTop: 8, backgroundColor: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
  recentItem: { padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #eee", fontSize: 15 },

  // Drawer
  drawerOverlay: { position: "fixed", top: 0, right: 0, bottom: 0, left: 0, backgroundColor: "rgba(0,0,0,0.3)", zIndex: 10000, display: "flex", justifyContent: "flex-start", fontFamily: "Arimo, sans-serif" },
  drawer: {
    width: "50%",
    maxWidth: 320,
    backgroundColor: "#fff",
    height: "100%",
    padding: 20,
    overflowY: "auto",
    boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
    transform: "translateX(-100%)",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // smoother ease-in-out
    fontFamily: "Arimo, sans-serif",
  },
  profileBox: { display: "flex", alignItems: "center", gap: 12, border: "1px solid #ddd", borderRadius: 12, padding: 12, marginBottom: 20 },
  avatarCircle: { width: 40, height: 40, borderRadius: "50%", backgroundColor: "#e91e63", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 18 },
  userName: { fontWeight: 600, fontSize: 16 },
  userMobile: { fontSize: 14, color: "#666" },
  sectionTitle: { marginTop: 25, marginBottom: 8, fontWeight: 600, fontSize: 16, color: "#333" },
  listItem: { padding: "14px 0", borderBottom: "1px solid #eee", cursor: "pointer" },
  footerLink: { padding: "15px 0", borderBottom: "1px solid #eee", cursor: "pointer", fontSize: 17, color: "#444" },
};

export default Header;
